// Classification admin data access — wraps the 13 classification
// endpoints under `apps/backend/src/classification/**`:
//   GET    /asset-systems
//   GET    /asset-systems/:systemId/sub-systems
//   GET    /asset-sub-systems/:subSystemId/asset-types
//   GET    /asset-types/search
//   GET    /classification/search
//   GET    /classification/taxonomy
//   POST   /admin/asset-systems
//   POST   /admin/asset-systems/:systemId/sub-systems
//   POST   /admin/asset-sub-systems/:subSystemId/asset-types
//   PUT    /admin/asset-types/:id
//   DELETE /admin/asset-systems/:systemId
//   DELETE /admin/asset-sub-systems/:subSystemId
//   DELETE /admin/asset-types/:typeId
//
// Response and input shapes mirror the backend contracts. They are
// redeclared here rather than imported because the monorepo
// dependency rules forbid a frontend→backend edge (AGENTS.md
// §Monorepo Structure). A future centralisation into
// `@citycare/shared` is tracked in #18 — until then, contract drift
// between this file and `apps/backend/src/classification/*.ts` is
// caught by backend contract tests first and manifested here as a
// typecheck failure when the cross-reference is bumped.

import type { GroupingConvention, UnitOfMeasure } from '@citycare/shared'

import { ApiError, apiFetch, type ApiFetchInit } from './api'

// ----- DTOs — mirror backend response shapes verbatim -------------

// list-systems.ts :: AssetSystemListRow
export interface AssetSystemListRow {
  id: string
  name: string
  code: string | null
  displayOrder: number
}

export interface AssetSystemsResponse {
  systems: AssetSystemListRow[]
}

// list-sub-systems.ts :: AssetSubSystemListRow
export interface AssetSubSystemListRow {
  id: string
  systemId: string
  name: string
  code: string | null
  displayOrder: number
}

export interface AssetSubSystemsResponse {
  subSystems: AssetSubSystemListRow[]
}

// list-types.ts :: AssetTypeListRow
export interface AssetTypeListRow {
  id: string
  subSystemId: string
  name: string
  code: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention: GroupingConvention
  isFieldAdded: boolean
  requiresReview: boolean
  displayOrder: number
}

export interface AssetTypesResponse {
  assetTypes: AssetTypeListRow[]
  total: number
}

// search-types.ts :: AssetTypeSearchRow
export interface AssetTypeSearchRow {
  id: string
  name: string
  code: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention: GroupingConvention
  isFieldAdded: boolean
  requiresReview: boolean
  subSystemId: string
  subSystemName: string
  systemId: string
  systemName: string
}

export interface AssetTypeSearchResponse {
  results: AssetTypeSearchRow[]
  total: number
}

// search-classification.ts :: per-level match rows + response shape
export interface ClassificationSearchSystemRow {
  id: string
  name: string
  code: string | null
  displayOrder: number
}

export interface ClassificationSearchSubSystemRow {
  id: string
  systemId: string
  systemName: string
  name: string
  code: string | null
  displayOrder: number
}

export interface ClassificationSearchAssetTypeRow {
  id: string
  name: string
  code: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention: GroupingConvention
  isFieldAdded: boolean
  requiresReview: boolean
  subSystemId: string
  subSystemName: string
  systemId: string
  systemName: string
}

export interface ClassificationSearchResponse {
  systems: ClassificationSearchSystemRow[]
  subSystems: ClassificationSearchSubSystemRow[]
  assetTypes: ClassificationSearchAssetTypeRow[]
}

// taxonomy.ts :: TaxonomySystemRow / TaxonomyResponse — single
// round-trip eager load for the picker's browse tree.
export interface TaxonomySubSystemRow {
  id: string
  systemId: string
  name: string
  code: string | null
  displayOrder: number
}

export interface TaxonomySystemRow {
  id: string
  name: string
  code: string | null
  displayOrder: number
  subSystems: TaxonomySubSystemRow[]
}

export interface TaxonomyResponse {
  systems: TaxonomySystemRow[]
}

// ----- Create inputs / response shapes ----------------------------

// create-system.ts :: AssetSystemCreateInput + CreatedAssetSystem
export interface AssetSystemCreateInput {
  name: string
  code?: string | null
  displayOrder?: number
}

export interface CreatedAssetSystem {
  id: string
  contractId: string
  name: string
  code: string | null
  displayOrder: number
}

// create-sub-system.ts :: AssetSubSystemCreateInput + CreatedAssetSubSystem
export interface AssetSubSystemCreateInput {
  name: string
  code?: string | null
  displayOrder?: number
}

export interface CreatedAssetSubSystem {
  id: string
  systemId: string
  name: string
  code: string | null
  displayOrder: number
}

// create-type.ts :: AssetTypeCreateInput + CreatedAssetType
export interface AssetTypeCreateInput {
  name: string
  code?: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention?: GroupingConvention
  displayOrder?: number
}

export interface CreatedAssetType {
  id: string
  subSystemId: string
  name: string
  code: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention: GroupingConvention
  isFieldAdded: boolean
  requiresReview: boolean
  displayOrder: number
}

// update-asset-type.ts :: AssetTypeUpdateInput + UpdatedAssetType
// All fields are optional — caller sends only what changed.
export interface AssetTypeUpdateInput {
  name?: string
  code?: string | null
  unitOfMeasure?: UnitOfMeasure
  groupingConvention?: GroupingConvention
  displayOrder?: number
}

export interface UpdatedAssetType {
  id: string
  subSystemId: string
  name: string
  code: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention: GroupingConvention
  isFieldAdded: boolean
  requiresReview: boolean
  displayOrder: number
}

// Delete response (all three levels share {id, rejectedAt}).
export interface DeletedClassificationRow {
  id: string
  rejectedAt: string
}

// ----- Pagination bounds matching list-types.ts -------------------
export const ASSET_TYPES_DEFAULT_TAKE = 50
export const ASSET_TYPES_MAX_TAKE = 200
// search-types.ts uses default 25, max 100 — the UI default keeps
// the search results below the list pagination so the bar feels
// snappy; a future "see more results" lever can expose the 100.
export const ASSET_TYPES_SEARCH_DEFAULT_TAKE = 25
export const ASSET_TYPES_SEARCH_MAX_TAKE = 100
export const ASSET_TYPE_SEARCH_MIN_QUERY = 2

// ----- Typed error for cascade-conflict (HTTP 409 equivalents) ----

// Backend uses `APIError.failedPrecondition(...)` which Encore maps
// to HTTP 400 with `code: 'failed_precondition'` in the error body
// (see apps/backend/src/classification/delete-system.ts and
// delete-sub-system.ts). The PRD treats this as a "409 cascade
// conflict" for admin UI purposes — the level still has children,
// and the user must clean them up top-down. We parse the message
// suffix ("has N non-rejected …") to surface the blocking count
// without requiring the backend to add a structured field.
export class CascadeConflictError extends Error {
  readonly blockingCount: number
  readonly cause?: ApiError

  constructor(blockingCount: number, message: string, cause?: ApiError) {
    super(message)
    this.name = 'CascadeConflictError'
    this.blockingCount = blockingCount
    this.cause = cause
  }
}

// Narrow predicate matching the `failed_precondition` code Encore
// emits, plus a defensive status check for deployments that surface
// 409 directly. Other ApiError codes fall through untouched so the
// UI can render them via the generic error path.
function isCascadeConflict(err: ApiError): boolean {
  return err.code === 'failed_precondition' || err.status === 409
}

function parseBlockingCount(message: string): number {
  // Backend message shape:
  //   "asset system X has 4 non-rejected sub-system(s); ..."
  //   "asset sub-system X has 12 non-rejected asset type(s); ..."
  const match = /has (\d+) non-rejected/u.exec(message)
  if (match === null) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isNaN(n) ? 0 : n
}

async function withCascadeMapping<T>(promise: Promise<T>): Promise<T> {
  try {
    return await promise
  } catch (err) {
    if (err instanceof ApiError && isCascadeConflict(err)) {
      throw new CascadeConflictError(
        parseBlockingCount(err.message),
        err.message,
        err,
      )
    }
    throw err
  }
}

// ----- Read endpoints ---------------------------------------------

export function fetchSystems(
  init: ApiFetchInit = {},
): Promise<AssetSystemsResponse> {
  return apiFetch<AssetSystemsResponse>('/asset-systems', init)
}

export function fetchTaxonomy(
  init: ApiFetchInit = {},
): Promise<TaxonomyResponse> {
  return apiFetch<TaxonomyResponse>('/classification/taxonomy', init)
}

export function fetchSubSystems(
  systemId: string,
  init: ApiFetchInit = {},
): Promise<AssetSubSystemsResponse> {
  return apiFetch<AssetSubSystemsResponse>(
    `/asset-systems/${encodeURIComponent(systemId)}/sub-systems`,
    init,
  )
}

export interface AssetTypesListQuery {
  skip?: number
  take?: number
}

export function fetchAssetTypes(
  subSystemId: string,
  query: AssetTypesListQuery = {},
  init: ApiFetchInit = {},
): Promise<AssetTypesResponse> {
  const params = new URLSearchParams()
  const take = clampTake(
    query.take ?? ASSET_TYPES_DEFAULT_TAKE,
    ASSET_TYPES_MAX_TAKE,
  )
  params.set('take', String(take))
  if (query.skip !== undefined && query.skip > 0) {
    params.set('skip', String(query.skip))
  }
  return apiFetch<AssetTypesResponse>(
    `/asset-sub-systems/${encodeURIComponent(subSystemId)}/asset-types?${params.toString()}`,
    init,
  )
}

export interface AssetTypeSearchQuery {
  q: string
  skip?: number
  take?: number
}

export function searchAssetTypes(
  query: AssetTypeSearchQuery,
  init: ApiFetchInit = {},
): Promise<AssetTypeSearchResponse> {
  const params = new URLSearchParams({ q: query.q })
  const take = clampTake(
    query.take ?? ASSET_TYPES_SEARCH_DEFAULT_TAKE,
    ASSET_TYPES_SEARCH_MAX_TAKE,
  )
  params.set('take', String(take))
  if (query.skip !== undefined && query.skip > 0) {
    params.set('skip', String(query.skip))
  }
  return apiFetch<AssetTypeSearchResponse>(
    `/asset-types/search?${params.toString()}`,
    init,
  )
}

export interface ClassificationSearchQuery {
  q: string
  take?: number
}

export function searchClassification(
  query: ClassificationSearchQuery,
  init: ApiFetchInit = {},
): Promise<ClassificationSearchResponse> {
  const params = new URLSearchParams({ q: query.q })
  const take = clampTake(
    query.take ?? ASSET_TYPES_SEARCH_DEFAULT_TAKE,
    ASSET_TYPES_SEARCH_MAX_TAKE,
  )
  params.set('take', String(take))
  return apiFetch<ClassificationSearchResponse>(
    `/classification/search?${params.toString()}`,
    init,
  )
}

function clampTake(value: number, max: number): number {
  if (!Number.isFinite(value) || value <= 0) return max
  return Math.min(Math.floor(value), max)
}

// ----- Create endpoints --------------------------------------------

export function createSystem(
  input: AssetSystemCreateInput,
): Promise<CreatedAssetSystem> {
  return apiFetch<CreatedAssetSystem>('/admin/asset-systems', {
    method: 'POST',
    json: input,
  })
}

export function createSubSystem(
  systemId: string,
  input: AssetSubSystemCreateInput,
): Promise<CreatedAssetSubSystem> {
  return apiFetch<CreatedAssetSubSystem>(
    `/admin/asset-systems/${encodeURIComponent(systemId)}/sub-systems`,
    { method: 'POST', json: input },
  )
}

export function createAssetType(
  subSystemId: string,
  input: AssetTypeCreateInput,
): Promise<CreatedAssetType> {
  return apiFetch<CreatedAssetType>(
    `/admin/asset-sub-systems/${encodeURIComponent(subSystemId)}/asset-types`,
    { method: 'POST', json: input },
  )
}

// ----- Update endpoints ------------------------------------------

export function updateAssetType(
  id: string,
  input: AssetTypeUpdateInput,
): Promise<UpdatedAssetType> {
  return apiFetch<UpdatedAssetType>(
    `/admin/asset-types/${encodeURIComponent(id)}`,
    { method: 'PUT', json: input },
  )
}

// ----- Delete endpoints (map cascade conflict) --------------------

export function deleteSystem(id: string): Promise<DeletedClassificationRow> {
  return withCascadeMapping(
    apiFetch<DeletedClassificationRow>(
      `/admin/asset-systems/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  )
}

export function deleteSubSystem(
  id: string,
): Promise<DeletedClassificationRow> {
  return withCascadeMapping(
    apiFetch<DeletedClassificationRow>(
      `/admin/asset-sub-systems/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  )
}

export function deleteAssetType(
  id: string,
): Promise<DeletedClassificationRow> {
  // Asset type deletion has no cascade gate per delete-type.ts, so
  // the mapping is a no-op for a compliant backend. Wrapping it
  // anyway keeps the call sites symmetrical and future-proofs the
  // path against a backend change that would start returning 409.
  return withCascadeMapping(
    apiFetch<DeletedClassificationRow>(
      `/admin/asset-types/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  )
}
